import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { HeadlineOverlay, HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { useHeadlineABWizardStore } from '@/store/headline-ab-test';
import { useAuth } from '@/hooks/useAuth';
import { createHeadlineTest } from '@/services/headlineTestService';
import { useUserHeadlineTests } from '@/hooks/useHeadlineTest';
import {
  HeadlineABWizardStepper,
  HeadlineABWizardFooter,
  HeadlineABVariationsStep,
  HeadlineABOrderStep,
  HeadlineABTrafficStep,
  HeadlineActiveTestPanel
} from './wizard';

const stepVariants = {
  initial: (direction: number) => ({
    x: direction > 0 ? 30 : -30,
    opacity: 0
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -30 : 30,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  })
};

interface HeadlineABTestTabProps {
  headlineOverlay: HeadlineOverlay;
  updateHeadlineOverlay: (key: keyof HeadlineOverlay, value: any) => void;
  canUseABTest: boolean;
  videoId?: string;
}

export const HeadlineABTestTab: React.FC<HeadlineABTestTabProps> = ({
  headlineOverlay,
  updateHeadlineOverlay,
  canUseABTest,
  videoId
}) => {
  useLanguage();
  const { currentStep, steps, data, setCurrentStep, nextStep, prevStep, completeStep, reset } = useHeadlineABWizardStore();
  const [direction, setDirection] = React.useState(0);
  const [showActivePanel, setShowActivePanel] = useState(false);
  const [isCreatingTest, setIsCreatingTest] = useState(false);

  const { user, token } = useAuth();
  const { data: userHeadlineTests } = useUserHeadlineTests(user?.id_user || user?.id);

  const abTestConfig = headlineOverlay.abTestConfig;
  const variations = abTestConfig.variations || [];

  // Mostrar painel ativo se o teste já estiver ativo
  useEffect(() => {
    if (abTestConfig.status === 'active') {
      setShowActivePanel(true);
    }
  }, [abTestConfig.status]);

  // Verificar expiração: quando o backend remove o teste (lazy expiry por end_date),
  // ele deixa de aparecer na listagem — o frontend detecta a ausência e finaliza localmente
  useEffect(() => {
    if (!abTestConfig.headlineTestId || abTestConfig.status !== 'active' || !abTestConfig.endDate) return;
    if (!userHeadlineTests) return;

    const stillExistsOnBackend = userHeadlineTests.some(
      (test: any) => test.id_headline_test === abTestConfig.headlineTestId
    );

    if (!stillExistsOnBackend) {
      updateABTestConfig({
        enabled: false,
        status: 'finished',
        endDate: new Date().toISOString()
      });
      setShowActivePanel(false);
      reset();
      toast.success(translateInterfaceText("Teste finalizado automaticamente"), {
        description: translateInterfaceText("A data de término foi atingida e o teste foi encerrado.")
      });
    }
  }, [userHeadlineTests, abTestConfig.headlineTestId, abTestConfig.status, abTestConfig.endDate]);

  const updateABTestConfig = (updates: Partial<HeadlineOverlay['abTestConfig']>) => {
    updateHeadlineOverlay('abTestConfig', {
      ...abTestConfig,
      ...updates
    });
  };

  const getTrafficPercentage = (variationId: string): number => {
    // Se houver distribuição personalizada, usar
    const equalShare = variations.length > 0 ? Math.floor(100 / variations.length) : 0;
    return equalShare;
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1: return variations.length >= 2;
      case 2: return variations.length >= 2;
      case 3: return true; // Validação feita no TrafficStep
      default: return false;
    }
  };

  const handleNext = () => {
    if (canGoNext()) {
      setDirection(1);
      completeStep(currentStep);
      nextStep();
    }
  };

  const handleBack = () => {
    setDirection(-1);
    prevStep();
  };

  const handleActivate = async () => {
    if (!videoId) {
      toast.error(translateInterfaceText("ID do vídeo não encontrado"));
      return;
    }

    if (!user || !token) {
      toast.error(translateInterfaceText("Usuário não autenticado"));
      return;
    }

    if (variations.length < 2) {
      toast.error(translateInterfaceText("É necessário ter pelo menos 2 variações para criar um teste A/B"));
      return;
    }

    setIsCreatingTest(true);

    try {
      // Mapear variações para o formato do backend
      const headlineTestConfig = variations.map(variation => ({
        // Configurações de headline
        headlineText: variation.headline?.text || '',
        subHeadlineText: variation.subheadline?.text || '',
        headlineColor: variation.headline?.color || '#ffffff',
        subHeadlineColor: variation.subheadline?.color || '#e5e5e5',
        headlineSize: typeof variation.headline?.size === 'number' ? variation.headline.size : 24,
        subHeadlineSize: typeof variation.subheadline?.size === 'number' ? variation.subheadline.size : 16,
        headlineTypeFont: variation.headline?.fontFamily || 'Inter',
        headlineFontWeight: variation.headline?.fontWeight || 'normal',

        // Forçar headline ativada
        haveHeadline: true,
      }));

      // Criar teste no backend
      const response = await createHeadlineTest(
        videoId,
        {
          headlineTestData: {
            headlineTestTitle: `Teste AB Headline - ${new Date().toLocaleDateString()}`,
            userId: user.id_user || user.id,
          },
          headlineTestConfig,
        },
        token
      );

      // Atualizar configuração local
      const newConfig = {
        enabled: true,
        status: 'active' as const,
        startDate: new Date().toISOString(),
        endDate: undefined,
        headlineTestId: response.id_headline_test,
      };

      updateABTestConfig(newConfig);
      setShowActivePanel(true);

      toast.success(translateInterfaceText("Teste A/B ativado!"), {
        description: translateInterfaceText("Copie o código do teste e atualize o embed no seu site para que ele comece a rodar.")
      });
    } catch (error: any) {
      console.error('Erro ao criar teste de headline:', error);
      toast.error(error.response?.data?.message || translateInterfaceText("Erro ao ativar teste A/B"));
    } finally {
      setIsCreatingTest(false);
    }
  };

  const handleFinalize = () => {
    updateABTestConfig({
      enabled: false,
      status: 'finished',
      endDate: new Date().toISOString()
    });
    setShowActivePanel(false);
    reset();
    toast.success(translateInterfaceText("Teste finalizado com sucesso!"));
  };

  // Se o teste está ativo, mostrar painel de teste ativo
  if (showActivePanel || abTestConfig.status === 'active') {
    return (
      <HeadlineActiveTestPanel
        headlineOverlay={headlineOverlay}
        variations={variations}
        onFinalize={handleFinalize}
        getTrafficPercentage={getTrafficPercentage}
        headlineTestId={abTestConfig.headlineTestId}
        token={token}
        videoId={videoId}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <HeadlineABVariationsStep
            variations={variations}
            headlineOverlay={headlineOverlay}
            onUpdateConfig={updateABTestConfig}
            getTrafficPercentage={getTrafficPercentage}
          />
        );
      case 2:
        return (
          <HeadlineABOrderStep
            variations={variations}
            onUpdateConfig={updateABTestConfig}
          />
        );
      case 3:
        return (
          <HeadlineABTrafficStep
            variations={variations}
            headlineOverlay={headlineOverlay}
            onUpdateConfig={updateABTestConfig}
            getTrafficPercentage={getTrafficPercentage}
            onActivate={handleActivate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 -mx-6">
      <HeadlineABWizardStepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={setCurrentStep}
      />
      
      <div className="flex-1 overflow-y-auto min-h-0 premium-scroll px-6 py-5">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      <HeadlineABWizardFooter
        currentStep={currentStep}
        totalSteps={steps.length}
        canGoNext={canGoNext()}
        onBack={handleBack}
        onNext={handleNext}
        onFinish={handleActivate}
        isFinishing={isCreatingTest}
      />
    </div>
  );
};
